'use client';

import React, { useState, useEffect } from 'react';
import { Rnd } from 'react-rnd';
import { ICamera } from '@/data/interfaces/camera';
import CameraControlsWinBox from './CameraControlsWinBox';
import { X } from 'lucide-react';

interface CameraControlsWindowProps {
  cameraId: string;
  cameraData: ICamera;
  isOpen: boolean;
  onClose: () => void;
  alertId?: string;
  locationId?: number;
  zIndex?: number;
}

const CameraControlsWindow: React.FC<CameraControlsWindowProps> = ({
  cameraId,
  cameraData,
  isOpen,
  onClose,
  alertId,
  locationId,
  zIndex = 1000
}) => {
  const [windowSize, setWindowSize] = useState({ width: 400, height: 500 });
  const [windowPosition, setWindowPosition] = useState({ x: 0, y: 0 });

  // Initialize window position to center of screen
  useEffect(() => {
    if (isOpen && windowPosition.x === 0 && windowPosition.y === 0) {
      const centerX = (window.innerWidth - windowSize.width) / 2;
      const centerY = (window.innerHeight - windowSize.height) / 2;
      setWindowPosition({ x: centerX, y: centerY });
    }
  }, [isOpen, windowSize, windowPosition]);

  // Load saved position and size from localStorage
  useEffect(() => {
    if (isOpen) {
      const savedPosition = localStorage.getItem(`camera-controls-${cameraId}-position`);
      const savedSize = localStorage.getItem(`camera-controls-${cameraId}-size`);
      
      if (savedPosition) {
        const { x, y } = JSON.parse(savedPosition);
        setWindowPosition({ x, y });
      }
      
      if (savedSize) {
        const { width, height } = JSON.parse(savedSize);
        setWindowSize({ width, height });
      }
    }
  }, [isOpen, cameraId]);

  const handleDragStop = (e: any, data: any) => {
    setWindowPosition({ x: data.x, y: data.y });
    localStorage.setItem(`camera-controls-${cameraId}-position`, JSON.stringify({ x: data.x, y: data.y }));
  };

  const handleResizeStop = (e: any, direction: any, ref: any, delta: any, position: any) => {
    const newSize = {
      width: ref.style.width,
      height: ref.style.height,
    };
    setWindowSize(newSize);
    setWindowPosition(position);
    
    localStorage.setItem(`camera-controls-${cameraId}-size`, JSON.stringify(newSize));
    localStorage.setItem(`camera-controls-${cameraId}-position`, JSON.stringify(position));
  };

  if (!isOpen) return null;

  return (
    <Rnd
      size={windowSize}
      position={windowPosition}
      onDragStop={handleDragStop}
      onResizeStop={handleResizeStop}
      minWidth={300}
      minHeight={400}
      maxWidth={800}
      maxHeight={700}
      bounds="window"
      dragHandleClassName="camera-controls-header"
      style={{ zIndex }}
      className="camera-controls-window"
    >
      <div className="h-full bg-slate-800 text-white rounded-lg shadow-2xl border border-slate-600 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="camera-controls-header bg-slate-700 px-4 py-3 border-b border-slate-600 flex items-center justify-between cursor-move">
          <h3 className="text-lg font-semibold text-white truncate">
            Camera Controls - {cameraData.name}
          </h3>
          <button
            onClick={onClose}
            className="ml-4 p-1 hover:bg-slate-600 rounded transition-colors duration-200 flex-shrink-0"
            title="Close"
            aria-label="Close Camera Controls"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          <CameraControlsWinBox
            cameraId={cameraId}
            cameraData={cameraData}
            alertId={alertId}
            locationId={locationId}
            onClose={onClose}
          />
        </div>
      </div>
    </Rnd>
  );
};

export default CameraControlsWindow;
